import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loader } from '@mantine/core'
import { Layout } from './components/common/Layout'
import { Dashboard } from './pages/dashboard/Dashboard'
// TODO: re-enable when Job Analytics and Documentation ship.
// import { Analytics } from './pages/analytics/Analytics'
// import { Documentation } from './pages/documentation/Documentation'
// TODO: re-enable when Runbooks ships.
// import { Runbooks } from './pages/runbooks/Runbooks'
import { Learning } from './pages/learning/Learning'
import { SignIn } from './pages/signin/SignIn'
import { GenieChat } from './pages/genie/GenieChat'
import { EmbeddedGenieChat } from './pages/genie/EmbeddedGenieChat'
import { IntelligenceHub } from './pages/intelligence/IntelligenceHub'
import { Products } from './pages/intelligence/Products'
import { DataCatalog } from './pages/data-catalog/DataCatalog'
import { QueryData } from './pages/query/QueryData'
import { SqlServerLineage } from './pages/sql-server-lineage/SqlServerLineage'
import { Lineage } from './pages/intelligence/Lineage'
import { Governance } from './pages/intelligence/Governance'
import { Requests } from './pages/requests/Requests'
import { MarkdownPage } from './components/common/MarkdownPage'
import { ComingSoon } from './components/common/ComingSoon'
// TODO: restore getRunbookBySlug when Runbooks ships, getDocBySlug when Documentation ships.
import { getLearningBySlug } from './content'
import {
  clearPrismAuth,
  getActiveAccount,
  isMsalAuthenticated,
} from './api/msal'
import { isAllowedSsoUser, isAllowlisted } from './api/allowlist'

/** Wraps a route element so non-allowlisted users see a redirect to dashboard. */
function RequireDatabricks({ children }: { children: React.ReactNode }) {
  if (isAllowlisted()) return <>{children}</>
  return <Navigate to="/dashboard" replace />
}

function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const [authState, setAuthState] = useState<
    'checking' | 'authenticated' | 'unauthenticated'
  >('checking')
  const [checkedPathname, setCheckedPathname] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setAuthState('checking')
    setCheckedPathname(null)
    isMsalAuthenticated()
      .then(async (ok) => {
        if (cancelled) return
        if (!ok) {
          clearPrismAuth()
        } else {
          // Refresh the allowlist flag so feature gates (RequireDatabricks)
          // stay current even across page reloads / tab restorations.
          const account = await getActiveAccount()
          if (account && !cancelled) {
            const allowed = await isAllowedSsoUser(account.username)
            localStorage.setItem(
              'prism_allowlisted',
              allowed ? 'true' : 'false'
            )
          }
        }
        if (cancelled) return
        setCheckedPathname(location.pathname)
        setAuthState(ok ? 'authenticated' : 'unauthenticated')
      })
      .catch(() => {
        if (cancelled) return
        clearPrismAuth()
        setCheckedPathname(location.pathname)
        setAuthState('unauthenticated')
      })
    return () => {
      cancelled = true
    }
  }, [navigate, location.pathname])

  if (authState === 'checking' || checkedPathname !== location.pathname) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader />
      </div>
    )
  }

  const isAuthenticated = authState === 'authenticated'

  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/genie/embedded"
                  element={<RequireDatabricks><EmbeddedGenieChat /></RequireDatabricks>}
                />
                {/* Retained while the embedded Genie Agent experience is
                    evaluated; hidden from the sidebar unless
                    VITE_ENABLE_LEGACY_GENIE_CHAT is set. */}
                <Route path="/genie" element={<RequireDatabricks><GenieChat /></RequireDatabricks>} />
                <Route
                  path="/data-intelligence"
                  element={<RequireDatabricks><IntelligenceHub /></RequireDatabricks>}
                />
                <Route
                  path="/intelligence/assets"
                  element={
                    <ComingSoon
                      title="Data Assets"
                      description="Governed data asset browsing is coming soon."
                    />
                  }
                />
                <Route path="/intelligence/products" element={<RequireDatabricks><Products /></RequireDatabricks>} />
                <Route
                  path="/intelligence/models"
                  element={
                    <ComingSoon
                      title="Models"
                      description="Model registry and governance views are coming soon."
                    />
                  }
                />
                <Route path="/intelligence/lineage" element={<RequireDatabricks><Lineage /></RequireDatabricks>} />
                <Route
                  path="/intelligence/governance"
                  element={<Governance />}
                />
                <Route
                  path="/intelligence/automation"
                  element={
                    <ComingSoon
                      title="AI & Automation"
                      description="AI agents and automation tooling are coming soon."
                    />
                  }
                />
                <Route
                  path="/intelligence/insights"
                  element={
                    <ComingSoon
                      title="Insights"
                      description="AI-generated insights and recommendations are coming soon."
                    />
                  }
                />
                <Route
                  path="/intelligence/search"
                  element={
                    <ComingSoon
                      title="Ask Prism"
                      description="Natural language search across the Prism data platform is coming soon."
                    />
                  }
                />
                <Route path="/data-catalog" element={<RequireDatabricks><DataCatalog /></RequireDatabricks>} />
                <Route path="/query" element={<RequireDatabricks><QueryData /></RequireDatabricks>} />
                <Route
                  path="/sql-server-lineage"
                  element={<SqlServerLineage />}
                />
                <Route
                  path="/analytics"
                  element={
                    <ComingSoon
                      title="Job Analytics"
                      description="Job analytics dashboards and monitoring are coming soon."
                    />
                  }
                />
                <Route
                  path="/documentation"
                  element={
                    <ComingSoon
                      title="Documentation"
                      description="Platform documentation and guides are coming soon."
                    />
                  }
                />
                {/* TODO: re-enable when Documentation ships.
                <Route
                  path="/documentation/:slug"
                  element={
                    <MarkdownPage
                      getBySlug={getDocBySlug}
                      backPath="/documentation"
                      backLabel="Back to Documentation"
                    />
                  }
                />
                */}
                {/* TODO: re-enable when Runbooks ships.
                <Route path="/runbooks" element={<Runbooks />} />
                <Route
                  path="/runbooks/:slug"
                  element={
                    <MarkdownPage
                      getBySlug={getRunbookBySlug}
                      backPath="/runbooks"
                      backLabel="Back to Runbooks"
                    />
                  }
                />
                */}
                <Route path="/learning" element={<Learning />} />
                <Route
                  path="/learning/:slug"
                  element={
                    <MarkdownPage
                      getBySlug={getLearningBySlug}
                      backPath="/learning"
                      backLabel="Back to Learning"
                    />
                  }
                />
                <Route path="/requests" element={<Requests />} />
                <Route
                  path="/settings"
                  element={
                    <ComingSoon
                      title="Settings"
                      description="Platform settings and configuration are coming soon."
                    />
                  }
                />
              </Routes>
            </Layout>
          ) : (
            <Navigate to="/signin" replace />
          )
        }
      />
    </Routes>
  )
}

export default App
